// EP-135 Tier 1 — Governance hooks (meetings, resolutions, committee).
// Mirrors the api/app/controllers/api/{meetings,resolutions,
// committee_members}_controller.rb shape. The 1-minute staleTime is the
// global default (see web/src/lib/queryClient.ts:11); nothing here needs
// longer for Tier 1 — voting / certification arrive in Tier 3/4.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  MeetingSummary,
  MeetingDetail,
  ResolutionSummary,
  ResolutionDetail,
  CommitteeMemberSummary,
  CommitteeRole,
  CommitteeMemberDetail,
  MeetingType,
  MeetingStatus,
  ResolutionStatus,
  NoticeSummary,
  NoticeDetail,
  NoticeChannel,
  NoticeDelivery,
  AttendeeSummary,
  AttendeeDetail,
  VoteSummary,
  VoteChoice,
  CastVia,
  IdentityLevel,
  AuditLogPayload,
  MinutesDocument,
  SigningRole,
} from '@/types/governance'

// ----------------------------------------------------------------------------
// Query key namespaces — keep cache invalidation targeted.
// ----------------------------------------------------------------------------
export const meetingKeys = {
  all: ['meetings'] as const,
  details: () => [ ...meetingKeys.all, 'detail' ] as const,
  detail: (id: number) => [ ...meetingKeys.details(), id ] as const,
}

export const resolutionKeys = {
  all: ['resolutions'] as const,
  byMeeting: (meetingId: number) =>
    [ ...resolutionKeys.all, 'by-meeting', meetingId ] as const,
  detail: (id: number) => [ ...resolutionKeys.all, 'detail', id ] as const,
}

export const committeeKeys = {
  all: ['committee_members'] as const,
  details: () => [ ...committeeKeys.all, 'detail' ] as const,
  detail: (id: number) => [ ...committeeKeys.details(), id ] as const,
}

// ----------------------------------------------------------------------------
// Meetings
// ----------------------------------------------------------------------------

interface MeetingListParams {
  page?: number
  meetingType?: MeetingType
  status?: MeetingStatus
  sort?: string
  order?: 'asc' | 'desc'
  propertyId?: number
}

export function useMeetings(params: MeetingListParams = {}) {
  const { propertyId, ...rest } = params
  return useQuery({
    queryKey: [ ...meetingKeys.all, 'list', params ],
    queryFn: async () => {
      // MON-18 C: follows the header's selected building (the controller
      // already accepts property_id); omitted entirely lists every building,
      // same as before.
      const queryParams: Record<string, unknown> = { ...rest }
      if (propertyId) queryParams.property_id = propertyId
      const response = await api.get('/api/meetings', { params: queryParams })
      return response.data as { data: MeetingSummary[]; meta: PagyMeta }
    },
  })
}

export function useMeeting(id: number) {
  return useQuery({
    queryKey: meetingKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${id}`)
      return response.data.data as MeetingDetail
    },
    enabled: !!id,
  })
}

interface MeetingInput {
  meeting_type: MeetingType
  title: string
  meeting_at: string
  agenda?: string | null
  venue?: string | null
  property_id?: number | null
  call_number?: number
  notice_lead_days?: number
  quorum_basis?: 'area_ratio' | 'unit_count'
  status?: MeetingStatus
}

export function useCreateMeeting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: MeetingInput) => {
      const response = await api.post('/api/meetings', { meeting: input })
      return response.data.data as MeetingDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
    },
  })
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<MeetingInput> & { id: number }) => {
      const response = await api.patch(`/api/meetings/${id}`, { meeting: input })
      return response.data.data as MeetingDetail
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.setQueryData(meetingKeys.detail(meeting.id), meeting)
    },
  })
}

export function useSendForCertification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/api/meetings/${id}/send_for_certification`)
      return response.data.data as MeetingDetail
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.setQueryData(meetingKeys.detail(meeting.id), meeting)
    },
  })
}

// ----------------------------------------------------------------------------
// Resolutions (nested under meeting)
// ----------------------------------------------------------------------------

export function useResolutions(meetingId: number) {
  return useQuery({
    queryKey: resolutionKeys.byMeeting(meetingId),
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${meetingId}/resolutions`)
      return response.data.data as ResolutionSummary[]
    },
    enabled: !!meetingId,
  })
}

export function useResolution(meetingId: number, id: number) {
  return useQuery({
    queryKey: resolutionKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(
        `/api/meetings/${meetingId}/resolutions/${id}`,
      )
      return response.data.data as ResolutionDetail
    },
    enabled: !!id,
  })
}

interface ResolutionInput {
  seq: number
  title: string
  body?: string | null
  note?: string | null
  decided_by?: 'vote' | 'consensus' | 'chair_ruling' | 'noted'
  threshold_rule?: string
  threshold_basis?: 'of_attendees' | 'of_all_owners'
  secret?: boolean
  status?: ResolutionStatus
  from_agenda?: boolean
}

export function useCreateResolution(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ResolutionInput) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/resolutions`,
        { resolution: input },
      )
      return response.data.data as ResolutionDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resolutionKeys.byMeeting(meetingId) })
    },
  })
}

export function useUpdateResolution(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<ResolutionInput> & { id: number }) => {
      const response = await api.patch(
        `/api/meetings/${meetingId}/resolutions/${id}`,
        { resolution: input },
      )
      return response.data.data as ResolutionDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resolutionKeys.byMeeting(meetingId) })
    },
  })
}

// ----------------------------------------------------------------------------
// Committee members
// ----------------------------------------------------------------------------

interface CommitteeListParams {
  page?: number
  active?: boolean
  role?: CommitteeRole
  sort?: string
  order?: 'asc' | 'desc'
}

export function useCommitteeMembers(params: CommitteeListParams = {}) {
  return useQuery({
    queryKey: [ ...committeeKeys.all, 'list', params ],
    queryFn: async () => {
      const response = await api.get('/api/committee_members', { params })
      return response.data as { data: CommitteeMemberSummary[]; meta: PagyMeta }
    },
  })
}

export function useCommitteeMember(id: number) {
  return useQuery({
    queryKey: committeeKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(`/api/committee_members/${id}`)
      return response.data.data as CommitteeMemberDetail
    },
    enabled: !!id,
  })
}

interface CommitteeInput {
  customer_id: number
  role: CommitteeRole
  elected_on?: string | null
  term_starts_on?: string | null
  term_ends_on?: string | null
  resolution_id?: number | null
}

export function useCreateCommitteeMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CommitteeInput) => {
      const response = await api.post('/api/committee_members', {
        committee_member: input,
      })
      return response.data.data as CommitteeMemberDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: committeeKeys.all })
    },
  })
}

export function useUpdateCommitteeMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<CommitteeInput> & { id: number }) => {
      const response = await api.patch(`/api/committee_members/${id}`, {
        committee_member: input,
      })
      return response.data.data as CommitteeMemberDetail
    },
    onSuccess: (member) => {
      queryClient.invalidateQueries({ queryKey: committeeKeys.all })
      queryClient.setQueryData(committeeKeys.detail(member.id), member)
    },
  })
}

export function useEndCommitteeTerm() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ended_on,
      reason,
    }: { id: number; ended_on?: string; reason: string }) => {
      const response = await api.post(`/api/committee_members/${id}/end_term`, {
        ended_on,
        reason,
      })
      return response.data.data as CommitteeMemberDetail
    },
    onSuccess: (member) => {
      queryClient.invalidateQueries({ queryKey: committeeKeys.all })
      queryClient.setQueryData(committeeKeys.detail(member.id), member)
    },
  })
}

// ----------------------------------------------------------------------------
// Local type alias (mirrors the API pagination envelope).
// ----------------------------------------------------------------------------
interface PagyMeta {
  total_count: number
  total_pages: number
  current_page: number
  per_page: number
}

// ----------------------------------------------------------------------------
// EP-135 Tier 2 — Notices + per-recipient deliveries. A notice is
// SERVED (one delivery row per audience member), not broadcast. The
// list view returns the audience/delivered/failed/pending counts so
// the operator sees proof-of-service status at a glance without
// pulling the per-recipient list. Detail view carries the delivery
// rows for the receipt table.
// ----------------------------------------------------------------------------

export const noticeKeys = {
  all: ['notices'] as const,
  byMeeting: (meetingId: number) =>
    [ ...noticeKeys.all, 'by-meeting', meetingId ] as const,
  details: () => [ ...noticeKeys.all, 'detail' ] as const,
  detail: (id: number) => [ ...noticeKeys.details(), id ] as const,
}

export function useNotices(meetingId: number) {
  return useQuery({
    queryKey: noticeKeys.byMeeting(meetingId),
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${meetingId}/notices`)
      return response.data.data as NoticeSummary[]
    },
    enabled: !!meetingId,
  })
}

export function useNotice(meetingId: number, id: number) {
  return useQuery({
    queryKey: noticeKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(
        `/api/meetings/${meetingId}/notices/${id}`,
      )
      return response.data.data as NoticeDetail
    },
    enabled: !!id,
  })
}

interface NoticeInput {
  body: string
  channel?: NoticeChannel
}

export function useCreateNotice(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: NoticeInput) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/notices`,
        { notice: input },
      )
      return response.data.data as NoticeDetail
    },
    onSuccess: (notice) => {
      queryClient.invalidateQueries({ queryKey: noticeKeys.byMeeting(meetingId) })
      queryClient.setQueryData(noticeKeys.detail(notice.id), notice)
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
    },
  })
}

interface NoticeDeliveryUpdateInput {
  delivered_at?: string | null
  failed_reason?: string | null
}

export function useUpdateNoticeDelivery(meetingId: number, noticeId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: NoticeDeliveryUpdateInput & { id: number }) => {
      const response = await api.patch(
        `/api/meetings/${meetingId}/notices/${noticeId}/deliveries/${id}`,
        { notice_delivery: input },
      )
      return response.data.data as NoticeDelivery
    },
    onSuccess: () => {
      // Refresh the notice detail so the per-row counts + delivery
      // table re-render together. The list (summary) view's counts
      // also depend on the delivery rows, so refresh both.
      queryClient.invalidateQueries({ queryKey: noticeKeys.detail(noticeId) })
      queryClient.invalidateQueries({ queryKey: noticeKeys.byMeeting(meetingId) })
    },
  })
}

// ----------------------------------------------------------------------------
// EP-135 Tier 3 — Attendees, Votes, Close-voting, Carry-over, Audit log.
//
// Attendees are PER-MEETING snapshots of an Owner's share-ratio weight
// (D-10). They are NOT a Person: a single Customer may hold multiple
// units and therefore carry weight on multiple attendee rows; the
// snapshot is the audit-of-record at notice-issue time.
//
// Voting is dual-surface per §12 S-4 + D-6:
//   - cast_via: self   — customer app, customer-authenticated
//   - cast_via: recorded — operator app, admin+ only (a customer
//     cannot self-attest or has no app access)
//
// `close_voting` freezes the per-resolution `tally_snapshot` (§3b —
// the chair certifies a snapshot, NEVER a live tally) and transitions
// the meeting to `voting_closed`. `carry_over_to_next_call` (D-13)
// creates a call-2 meeting and flips any unresolved resolutions to
// `deferred`.
// ----------------------------------------------------------------------------

export const attendeeKeys = {
  all: ['attendees'] as const,
  byMeeting: (meetingId: number) =>
    [ ...attendeeKeys.all, 'by-meeting', meetingId ] as const,
  detail: (id: number) => [ ...attendeeKeys.all, 'detail', id ] as const,
}

export const voteKeys = {
  all: ['votes'] as const,
  byResolution: (resolutionId: number) =>
    [ ...voteKeys.all, 'by-resolution', resolutionId ] as const,
}

export function useAttendees(meetingId: number) {
  return useQuery({
    queryKey: attendeeKeys.byMeeting(meetingId),
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${meetingId}/attendees`)
      return response.data.data as AttendeeSummary[]
    },
    enabled: !!meetingId,
  })
}

export function useAttendee(meetingId: number, id: number) {
  return useQuery({
    queryKey: attendeeKeys.detail(id),
    queryFn: async () => {
      const response = await api.get(
        `/api/meetings/${meetingId}/attendees/${id}`,
      )
      return response.data.data as AttendeeDetail
    },
    enabled: !!id,
  })
}

interface AttendeeInput {
  customer_id: number
  weight_snapshot: string
  unit_snapshot: Array<{ unit_id: number; ratio: string }> | null
  presence?: 'absent' | 'remote' | 'in_person' | 'proxy'
  proxy_holder_id?: number | null
  proxy_document_key?: string | null
  present?: boolean
}

export function useCreateAttendee(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: AttendeeInput) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/attendees`,
        { attendee: input },
      )
      return response.data.data as AttendeeDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendeeKeys.byMeeting(meetingId) })
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
    },
  })
}

export function useUpdateAttendee(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<AttendeeInput> & { id: number }) => {
      const response = await api.patch(
        `/api/meetings/${meetingId}/attendees/${id}`,
        { attendee: input },
      )
      return response.data.data as AttendeeDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendeeKeys.byMeeting(meetingId) })
    },
  })
}

export function useDeleteAttendee(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/meetings/${meetingId}/attendees/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendeeKeys.byMeeting(meetingId) })
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
    },
  })
}

// `snapshot` — recompute the per-unit weight + share-ratio for every
// attendee from the live OwnerContract roll (only allowed on a meeting
// whose status is still draft|noticed|in_progress; once votes are cast
// the snapshot is frozen for audit).
export function useSnapshotAttendees(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/api/meetings/${meetingId}/attendees/snapshot`,
      )
      return response.data.data as AttendeeSummary[]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attendeeKeys.byMeeting(meetingId) })
    },
  })
}

interface VotesParams {
  choice?: VoteChoice
}

export function useVotes(meetingId: number, resolutionId: number, params: VotesParams = {}) {
  return useQuery({
    queryKey: [ ...voteKeys.byResolution(resolutionId), params ],
    queryFn: async () => {
      const response = await api.get(
        `/api/meetings/${meetingId}/resolutions/${resolutionId}/votes`,
        { params },
      )
      return response.data.data as VoteSummary[]
    },
    enabled: !!meetingId && !!resolutionId,
  })
}

interface CastRecordedVoteInput {
  attendee_id: number
  choice: VoteChoice
}

export function useCastRecordedVote(meetingId: number, resolutionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CastRecordedVoteInput) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/resolutions/${resolutionId}/votes`,
        { vote: input },
      )
      return response.data.data as VoteSummary
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: voteKeys.byResolution(resolutionId) })
      queryClient.invalidateQueries({ queryKey: resolutionKeys.byMeeting(meetingId) })
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
    },
  })
}

// §3b — chair freezes `tally_snapshot` per resolution and transitions
// the meeting to `voting_closed`. The body returned is the meeting with
// each resolution's tally_snapshot now filled in.
export function useCloseVoting(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/meetings/${meetingId}/close_voting`)
      return response.data.data as MeetingDetail
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.setQueryData(meetingKeys.detail(meeting.id), meeting)
      queryClient.invalidateQueries({ queryKey: resolutionKeys.byMeeting(meetingId) })
    },
  })
}

// D-13 — carry unresolved resolutions into a fresh call-2 meeting.
// Allowed only when the source meeting is still draft|noticed|in_progress
// and call_number == 1.
export function useCarryOverToNextCall(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { meeting_at: string }) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/carry_over_to_next_call`,
        input,
      )
      return response.data.data as MeetingDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
      queryClient.invalidateQueries({ queryKey: resolutionKeys.byMeeting(meetingId) })
    },
  })
}

// §8b — immutable ETDA audit trail; per §4a secret ballots hide
// attendee_id + customer_name + recorded_by_id at the serializer.
export function useMeetingAuditLog(meetingId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [ ...meetingKeys.detail(meetingId), 'audit_log' ],
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${meetingId}/audit_log`)
      return response.data.data as AuditLogPayload
    },
    enabled: enabled && !!meetingId,
  })
}

// EP-135 Tier 4 — minutes document + signing.
//
// `useMeetingMinutes` reads the Minutes::DocumentBuilder payload —
// header, attendance, resolutions + tally, certification. The
// `metadata.draft` flag controls the DRAFT watermark until the
// meeting is `certified` (D-3).
//
// `useSignMinutes` records a single e-sign (chair or secretary) on
// the minutes; once BOTH have signed the controller transitions the
// meeting to `certified` atomically (§8 Q-5 dual sign).
//
// `useResetMinutesSignature` clears a single signer's stamp so the
// meeting can be re-signed (refused on a `certified` meeting — audit
// trail is immutable).
export function useMeetingMinutes(meetingId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [ ...meetingKeys.detail(meetingId), 'minutes' ],
    queryFn: async () => {
      const response = await api.get(`/api/meetings/${meetingId}/minutes`)
      return response.data.data as MinutesDocument
    },
    enabled: enabled && !!meetingId,
  })
}

interface SignMinutesInput {
  role: SigningRole
  signed_by_id: number
  certificate_pdf_key?: string
}

export function useSignMinutes(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SignMinutesInput) => {
      const response = await api.post(`/api/meetings/${meetingId}/sign_minutes`, input)
      return response.data.data as MeetingDetail
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.setQueryData(meetingKeys.detail(meeting.id), meeting)
      queryClient.invalidateQueries({
        queryKey: [ ...meetingKeys.detail(meetingId), 'minutes' ],
      })
    },
  })
}

export function useResetMinutesSignature(meetingId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (role: SigningRole) => {
      const response = await api.post(
        `/api/meetings/${meetingId}/reset_minutes_signature`,
        { role },
      )
      return response.data.data as MeetingDetail
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: meetingKeys.all })
      queryClient.setQueryData(meetingKeys.detail(meeting.id), meeting)
      queryClient.invalidateQueries({
        queryKey: [ ...meetingKeys.detail(meetingId), 'minutes' ],
      })
    },
  })
}

// Re-export the cast_via / identity level types so consumers do not
// have to dig into the types module just to switch on cast_via.
export type { CastVia, IdentityLevel }
